import {
  ComponentPropsWithoutRef,
  KeyboardEvent,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { LayoutUtilProps } from "../../types";
import { TabButton } from "./TabButton";
import { TabList } from "./TabList";
import { TabPanel } from "./TabPanel";
import {
  useLayoutPropsUtil,
  useOptionallyControlledState,
} from "../../internal/hooks";
import { TabContext } from "./internal/TabContext";
import { AnimatePresence, motion } from "motion/react";
import { useMergeRefs } from "../../hooks";

import cx from "classnames";
import styles from "./Tab.module.scss";

/**
 * Props for the Tab component
 * @extends Omit<ComponentPropsWithoutRef<"div">, "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag">
 * @extends LayoutUtilProps
 */
export type TabProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag"
> &
  LayoutUtilProps & {
    /**
     * The index of the tab that should be active by default.
     * Use for uncontrolled usage. For controlled usage, use `index` instead.
     * @default 0
     */
    defaultIndex?: number;
    /**
     * The index of the currently active tab.
     * Use for controlled usage. For uncontrolled usage, use `defaultIndex` instead.
     */
    index?: number;
    /**
     * Callback fired when the active tab changes.
     * Use with `index` for controlled usage.
     * @param index The index of the newly active tab
     */
    onIndexChange?: (index: number) => void;
    /**
     * Whether the tabs should fill the available width.
     * @default false
     */
    fill?: boolean;
  };

/**
 * Tab component for organizing content into multiple sections with navigation.
 *
 * Features:
 * - Keyboard navigation support (arrow keys, Enter, Space)
 * - Accessible with proper ARIA roles and attributes
 * - Smooth animations and transitions
 * - RTL language support
 * - Automatic focus management
 * - Scroll behavior for overflow tabs
 * - Compound component pattern with Tab.Button, Tab.List, and Tab.Panel
 * - Supports controlled state via `index` and `onIndexChange` props
 * - Supports layout utilities for positioning and spacing
 *
 * @example
 * // Uncontrolled
 * <Tab defaultIndex={0}>
 *   <Tab.List>
 *     <Tab.Button id="tab1" controls="panel1">First Tab</Tab.Button>
 *     <Tab.Button id="tab2" controls="panel2">Second Tab</Tab.Button>
 *   </Tab.List>
 *   <Tab.Panel id="panel1">Content for first tab</Tab.Panel>
 *   <Tab.Panel id="panel2">Content for second tab</Tab.Panel>
 * </Tab>
 *
 * @example
 * // Controlled
 * <Tab index={activeIndex} onIndexChange={setActiveIndex}>
 *   <Tab.List>
 *     <Tab.Button id="tab1" controls="panel1">First Tab</Tab.Button>
 *     <Tab.Button id="tab2" controls="panel2">Second Tab</Tab.Button>
 *   </Tab.List>
 *   <Tab.Panel id="panel1">Content for first tab</Tab.Panel>
 *   <Tab.Panel id="panel2">Content for second tab</Tab.Panel>
 * </Tab>
 */
export const Tab = Object.assign(
  forwardRef<HTMLDivElement, TabProps>(function TabInner(props, ref) {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      defaultIndex,
      index,
      onIndexChange,
      className,
      style,
      fill,
      ...rest
    } = componentProps;
    const tabRef = useRef<HTMLDivElement>(null);
    const [items, setItems] = useState<NodeListOf<HTMLElement>>();
    const [current, setCurrent] = useOptionallyControlledState<number>({
      controlledValue: index,
      defaultValue: defaultIndex ?? 0,
      onChange: onIndexChange,
    });
    // Controlled mode is determined at render time; switching between controlled and uncontrolled at runtime is not supported.
    const isControlled = index !== undefined;
    const [focus, setFocus] = useState<number>(defaultIndex ?? 0);
    const [dir, setDir] = useState("ltr");
    const uid = useId();

    useEffect(() => {
      setItems(tabRef.current?.querySelectorAll("[role=tab]:not([disabled])"));
    }, [tabRef]);

    useEffect(() => {
      if (tabRef.current?.closest("[dir]")?.getAttribute("dir") === "rtl") {
        setDir("rtl");
      } else {
        setDir("ltr");
      }
    }, []);

    useEffect(() => {
      if (!isControlled) return;
      const tablistEl = tabRef.current?.querySelector("[role=tablist]");
      const hasFocusInTablist =
        tablistEl?.contains(document.activeElement) ?? false;
      if (!hasFocusInTablist) setFocus(index!);
    }, [index, isControlled]);

    useEffect(() => {
      if (!tabRef.current) return;

      const mutationCallback = () => {
        setItems(
          tabRef.current?.querySelectorAll("[role=tab]:not([disabled])"),
        );
      };

      const observer = new MutationObserver(mutationCallback);
      observer.observe(tabRef.current, {
        childList: true, // Observe changes to child elements
        subtree: true, // Observe changes to descendants of the target
        attributes: true, // Observe changes to attributes
        characterData: true, // Observe changes to text content
      });
      return () => {
        observer.disconnect();
      };
    }, []);

    // scroll behavior of the focus tab item
    const focusScroller = (node: HTMLElement | undefined) => {
      node?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    };

    const gotoNext = useCallback(
      (_refocus = true) => {
        if (focus + 1 === items?.length) {
          setFocus?.(0);
          items?.[0].focus();
          focusScroller(items?.[0]);
          return;
        }
        setFocus?.(focus + 1);
        items?.[focus + 1].focus();
        focusScroller(items?.[focus + 1]);
      },
      [focus, items],
    );

    const goToPrev = useCallback(
      (_refocus = true) => {
        if (focus === 0) {
          const lastItemIndex = items ? items?.length - 1 : 0;
          setFocus?.(lastItemIndex);
          items?.[lastItemIndex].focus();
          focusScroller(items?.[lastItemIndex]);
          return;
        }
        setFocus?.(focus - 1);
        items?.[focus - 1].focus();
        focusScroller(items?.[focus - 1]);
      },
      [focus, items],
    );

    const onKeyDownHandler = useCallback(
      (e: KeyboardEvent<HTMLButtonElement>) => {
        switch (e.code) {
          case "Enter":
          case "Space":
            setCurrent?.(focus);
            break;
          case "ArrowRight":
            e.preventDefault();
            if (dir === "ltr") {
              gotoNext();
            } else {
              goToPrev();
            }
            break;
          case "ArrowLeft":
            e.preventDefault();
            if (dir === "ltr") {
              goToPrev();
            } else {
              gotoNext();
            }
            break;
          default:
            break;
        }
      },
      [dir, focus, goToPrev, gotoNext, setCurrent],
    );

    const value = useMemo(
      () => ({
        current,
        setCurrent,
        focus,
        setFocus,
        items,
        uid,
        onKeyDownHandler,
        gotoNext,
        goToPrev,
        dir,
      }),
      [
        current,
        setCurrent,
        focus,
        goToPrev,
        gotoNext,
        items,
        onKeyDownHandler,
        uid,
        dir,
      ],
    );

    const tabClassNames = cx(styles["tab"], className, {
      [styles["fill"]]: fill,
    });

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    return (
      <TabContext.Provider value={value}>
        <AnimatePresence initial={false}>
          <motion.div
            transition={{ duration: 0 }}
            layout
            className={tabClassNames}
            data-anv="tab"
            style={styleCombined}
            {...rest}
            ref={useMergeRefs([tabRef, ref])}
          />
        </AnimatePresence>
      </TabContext.Provider>
    );
  }),
  {
    /**
     * TabButton component for individual tab navigation buttons.
     *
     * Features:
     * - Automatic ARIA attributes for accessibility
     * - Visual indicator for active state
     * - Keyboard navigation support
     * - Smooth animations with reduced motion support
     * - Proper focus management
     * - RTL language support
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <Tab.Button id="tab1" controls="panel1" onClick={(e, index) => console.log('Tab clicked:', index)}>
     *   First Tab
     * </Tab.Button>
     */
    Button: TabButton,
    /**
     * TabList component for containing and managing tab buttons.
     *
     * Features:
     * - Horizontal scrolling for overflow tabs
     * - Navigation arrows for overflow content
     * - RTL language support
     * - Automatic overflow detection
     * - Proper ARIA roles for accessibility
     * - Smooth scrolling behavior
     * - Visual indicator positioning
     *
     * @example
     * <Tab.List>
     *   <Tab.Button id="tab1" controls="panel1">First Tab</Tab.Button>
     *   <Tab.Button id="tab2" controls="panel2">Second Tab</Tab.Button>
     *   <Tab.Button id="tab3" controls="panel3">Third Tab</Tab.Button>
     * </Tab.List>
     */
    List: TabList,
    /**
     * TabPanel component for displaying tab content.
     *
     * Features:
     * - Automatic visibility based on active tab
     * - Smooth fade animations
     * - Proper ARIA attributes for accessibility
     * - Automatic association with tab buttons
     * - Focus management
     * - Layout animations
     *
     * @example
     * <Tab.Panel id="panel1">
     *   <h2>First Tab Content</h2>
     *   <p>This is the content for the first tab.</p>
     * </Tab.Panel>
     */
    Panel: TabPanel,
  },
);
Tab.displayName = "Tab";
